import React from 'react';
import { Link } from 'react-router-dom';
import { Utensils, ChefHat, ArrowRight, CalendarClock } from 'lucide-react';
import { useDossierContext } from './SuiviPrivatisationLayout';
import { COLLECTE_STATUS_LABELS } from '../Evenementiel/collecteStatus';
import { PIPELINE_STAGE_LABELS, hasPassedPivot } from './pipelineStages';
import { pillSecondary } from '../ui/pills';
import { moduleApi } from '../../lib/api';
import { ClotureDateModal } from '../Evenementiel/convives/ClotureDateModal';
import { OrganisateurAccesCard, useOrganisateurAcces } from '../Evenementiel/convives/organisateur/OrganisateurAccesCard';
import { OrganisateurAccesModal } from '../Evenementiel/convives/organisateur/OrganisateurAccesModal';

/**
 * Onglet « Menus & Jour J » — la partie du parcours qui appartient à d'autres modules.
 *
 * Rien n'est recalculé ici : le statut de la collecte est celui de Menus & Convives, lu tel quel depuis son
 * registre (`collecteStatus.ts`), et le Jour J renvoie vers le Mode terrain. Redupliquer les douze statuts
 * de la collecte aurait garanti qu'ils divergent au premier changement.
 */
export function DossierMenusPage() {
    const { dossier } = useDossierContext();
    // Sans privatisation dans Événementiel, ni la collecte ni le Mode terrain n'ont d'objet : tous deux
    // travaillent sur un `event_id`. C'est vrai jusqu'à « Privatisation créée », pas seulement jusqu'à la
    // signature — d'où les deux conditions distinctes.
    const signe = hasPassedPivot(dossier.stage);
    const eventId = dossier.event?.id || null;

    // La cloture programmee et l'acces organisateur se reglent ICI aussi (Emeni, 18/09) : c'est a l'etape
    // ou l'on ouvre la collecte qu'on envoie ses identifiants provisoires a l'organisateur, et qu'on decide
    // de la date limite de reponse. Aucune logique n'est recopiee : ce sont les MEMES modales que Menus &
    // Convives, qui ecrivent au meme endroit.
    const [collecte, setCollecte] = React.useState<any>(null);
    const [modale, setModale] = React.useState<null | 'cloture' | 'organisateur'>(null);
    const orga = useOrganisateurAcces(eventId || '');
    const chargerCollecte = React.useCallback(() => {
        if (!eventId) return;
        // Un etablissement sans le module Menus & Convives recoit un refus : on n'affiche simplement pas
        // ces deux cartes, plutot que de montrer un bloc qui ne repondrait jamais.
        moduleApi.getEvenementielCollecte(eventId).then(setCollecte).catch(() => setCollecte(null));
    }, [eventId]);
    React.useEffect(() => { chargerCollecte(); }, [chargerCollecte]);

    const closeDate = collecte?.responses_close_date || '';
    const eventDay = String(dossier.event?.start_time || '').slice(0, 10);
    const dateLisible = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <div className="space-y-2.5">
            <div className="rounded-[10px] border border-border-soft bg-surface-soft p-3">
                <p className="text-[12.5px] font-semibold text-content flex items-center gap-1.5">
                    <Utensils size={13} /> Collecte des menus
                </p>
                {!signe ? (
                    // Le message dit la RÈGLE, pas seulement l'interdiction : « ce n'est pas encore possible »
                    // sans expliquer pourquoi se lit comme une panne, et fait ouvrir un ticket.
                    <p className="text-[12px] text-content-muted mt-1.5 leading-relaxed text-pretty">
                        La collecte démarre à la signature du devis. Ce dossier en est au stade
                        « {PIPELINE_STAGE_LABELS[dossier.stage]} » — on ne fait pas choisir son plat à un
                        convive pour un événement qui n'est pas encore vendu.
                    </p>
                ) : !eventId ? (
                    <p className="text-[12px] text-content-muted mt-1.5 leading-relaxed text-pretty">
                        Le devis est signé : créez la privatisation depuis le bouton en haut de page pour lui
                        donner sa date. La collecte se lancera ensuite.
                    </p>
                ) : (
                    // Dès que la privatisation existe, les deux gestes sont faisables ICI : configurer le
                    // menu (formule, date de cloture, message aux convives) et ouvrir la collecte. Avant,
                    // seul un lien sortant existait, et le menu ne se configurait que depuis Menus &
                    // Convives. Demande d'Emeni (18/09) : la configuration doit se faire des deux côtés.
                    <>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center h-[22px] px-[9px] rounded-md bg-state-info-soft text-state-info border border-state-info/30 text-[10px] font-bold uppercase tracking-[.06em] whitespace-nowrap">
                                {COLLECTE_STATUS_LABELS[dossier.collecte_status || 'menus_a_configurer'] || dossier.collecte_status}
                            </span>
                            <Link to={`/menus-convives/${eventId}/menu`} className={pillSecondary(32)}>
                                <Utensils size={13} /> Configurer le menu
                            </Link>
                            <Link to={`/menus-convives/${eventId}`} className={pillSecondary(32)}>Ouvrir Menus &amp; Convives</Link>
                        </div>
                        {!dossier.collecte_status && (
                            <p className="text-[12px] text-content-muted mt-1.5 leading-relaxed text-pretty">
                                Rien n'est encore réglé pour cette privatisation : commencez par rattacher la
                                formule, puis importez le fichier des convives.
                            </p>
                        )}
                    </>
                )}
            </div>

            {/* Les deux gestes de l'etape « Collecte des menus » : la date limite de reponse, et le lien
                provisoire de l'organisateur. Ils vivaient uniquement dans Menus & Convives ; le dossier est
                pourtant l'endroit ou l'on suit cette etape (Emeni, 18/09). */}
            {eventId && collecte && (
                <div className="rounded-[10px] border border-border-soft bg-surface-soft p-3">
                    <p className="text-[12.5px] font-semibold text-content flex items-center gap-1.5">
                        <CalendarClock size={13} /> Clôture programmée
                    </p>
                    <p className="text-[12px] text-content-muted mt-1.5 leading-relaxed text-pretty">
                        {collecte.responses_closed_at
                            ? 'Les réponses sont fermées : la cuisine part sur ce qui a été choisi.'
                            : closeDate
                                ? `Les convives ont jusqu'au ${dateLisible(closeDate)} pour répondre. Cette date est annoncée dans chaque courriel.`
                                : "Aucune date limite n'est réglée : vous clôturez à la main quand vous le souhaitez."}
                    </p>
                    {!collecte.responses_closed_at && (
                        <button type="button" onClick={() => setModale('cloture')} className={`${pillSecondary(32)} mt-2`}>
                            {closeDate ? 'Changer la date' : 'Régler la clôture'}
                        </button>
                    )}
                </div>
            )}

            {eventId && collecte && (
                <OrganisateurAccesCard actifs={orga.actifs} onGerer={() => setModale('organisateur')} />
            )}

            <div className="rounded-[10px] border border-border-soft bg-surface-soft p-3">
                <p className="text-[12.5px] font-semibold text-content flex items-center gap-1.5">
                    <ChefHat size={13} /> Jour J
                </p>
                <p className="text-[12px] text-content-muted mt-1.5 text-pretty">
                    L'organisation du service se pilote depuis le Mode terrain, dans le module Cuisine — c'est
                    lui qui clôt le dossier avec « Terminer le service ».
                </p>
                {eventId ? (
                    <Link to={`/mode-terrain/${eventId}`} className={`${pillSecondary(32)} mt-2`}>
                        Ouvrir le Mode terrain <ArrowRight size={12} />
                    </Link>
                ) : (
                    <p className="text-[11.5px] text-content-muted mt-1 italic">
                        Disponible une fois la privatisation créée.
                    </p>
                )}
            </div>

            {modale === 'cloture' && eventId && collecte && (
                <ClotureDateModal eventId={eventId} eventDay={eventDay} prewarningSentAt={collecte.prewarning_sent_at}
                    initial={{
                        closeDate,
                        prewarnDate: collecte.prewarning_date || '',
                        closeNotify: collecte.close_notify_enabled === undefined || collecte.close_notify_enabled === null ? true : !!collecte.close_notify_enabled,
                    }}
                    onSaved={() => { setModale(null); chargerCollecte(); }} onClose={() => setModale(null)} />
            )}
            {modale === 'organisateur' && eventId && (
                <OrganisateurAccesModal eventId={eventId} info={orga.info} onChange={orga.recharger} onClose={() => setModale(null)} />
            )}
        </div>
    );
}
